import * as THREE from "three";

// Classic 64x32 skin layout, painted per player. Roughly a third of players
// get the default Steve; the rest are recoloured Steves, like a 2015 server.

type RGB = [number, number, number];
const hex = (h: string): RGB => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
const css = (c: RGB, f = 1) => `rgb(${Math.min(255, c[0] * f) | 0},${Math.min(255, c[1] * f) | 0},${Math.min(255, c[2] * f) | 0})`;

export function hashStr(s: string) {
  let h = 2166136261;
  for (const ch of s) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return h >>> 0;
}

const STEVE = { skin: "#b78a6c", hair: "#2f200d", shirt: "#00a8a8", pants: "#3a3189", shoes: "#5f5f5f", eyes: "#4b3a8a" };
const SKINS = ["#b78a6c", "#c99a7a", "#9a6b4d", "#6e4a33", "#e0b393"];
const HAIRS = ["#2f200d", "#1a1a1a", "#7a4b1e", "#c8a050", "#8f2a12", "#d9d9d9"];
const SHIRTS = ["#00a8a8", "#2d8a2d", "#b02a2a", "#6a3fb0", "#d48a14", "#2c5cc4", "#c9c9c9", "#df5c9a", "#242424", "#e6e6e6"];
const PANTS = ["#3a3189", "#242424", "#4a3a2a", "#2d4f2d", "#5a2020", "#6b6b6b"];

export function paintSkin(name: string): HTMLCanvasElement {
  const h = hashStr(name);
  const pick = <V>(a: V[], s: number) => a[(h >>> s) % a.length]!;
  const steve = h % 3 === 0;
  const P = steve
    ? STEVE
    : { skin: pick(SKINS, 2), hair: pick(HAIRS, 5), shirt: pick(SHIRTS, 9), pants: pick(PANTS, 13), shoes: "#5f5f5f", eyes: (h >>> 17) % 2 ? "#4b3a8a" : "#5a3a1a" };
  const c = document.createElement("canvas");
  c.width = 64;
  c.height = 32;
  const g = c.getContext("2d")!;
  let s = h || 1;
  const rand = () => ((s = Math.imul(s ^ (s >>> 13), 0x5bd1e995) >>> 0) % 1000) / 1000;
  const fill = (x: number, y: number, w: number, hh: number, col: string, jitter = 0.08) => {
    const base = hex(col);
    for (let j = 0; j < hh; j++) for (let i = 0; i < w; i++) {
      g.fillStyle = css(base, 1 - jitter / 2 + rand() * jitter);
      g.fillRect(x + i, y + j, 1, 1);
    }
  };
  const px = (x: number, y: number, col: string) => {
    g.fillStyle = col;
    g.fillRect(x, y, 1, 1);
  };

  // Head (0,0): 8x8x8. Top/bottom row first, then right/front/left/back.
  fill(8, 0, 8, 8, P.hair);
  fill(16, 0, 8, 8, P.skin);
  for (const fx of [0, 16, 24]) {
    fill(fx, 8, 8, 8, P.skin);
    fill(fx, 8, 8, fx === 24 ? 6 : 3, P.hair);
  }
  // Face.
  const F = 8, Fy = 8;
  fill(F, Fy, 8, 8, P.skin);
  fill(F, Fy, 8, 2, P.hair);
  px(F, Fy + 2, css(hex(P.hair)));
  px(F + 7, Fy + 2, css(hex(P.hair)));
  for (const ex of [1, 5]) {
    px(F + ex, Fy + 4, "#ffffff");
    px(F + ex + 1, Fy + 4, P.eyes);
  }
  // Steve's nose and stubbly mouth.
  fill(F + 3, Fy + 5, 2, 1, css(hex(P.skin), 0.78), 0);
  fill(F + 2, Fy + 6, 4, 1, css(hex(P.skin), 0.55), 0);
  fill(F + 2, Fy + 7, 1, 1, css(hex(P.skin), 0.7), 0);
  fill(F + 5, Fy + 7, 1, 1, css(hex(P.skin), 0.7), 0);

  // Body (16,16): 8x12x4.
  fill(20, 16, 8, 4, P.shirt);
  fill(28, 16, 8, 4, P.shirt);
  fill(16, 20, 4, 12, P.shirt);
  fill(20, 20, 8, 12, P.shirt);
  fill(28, 20, 4, 12, P.shirt);
  fill(32, 20, 8, 12, P.shirt);
  // Neckline and the darker band where the shirt meets the pants.
  fill(23, 20, 2, 1, P.skin, 0);
  fill(20, 30, 8, 2, P.pants);
  fill(32, 30, 8, 2, P.pants);
  fill(16, 30, 4, 2, P.pants);
  fill(28, 30, 4, 2, P.pants);

  // Arm (40,16): 4x12x4. Short sleeves, then skin.
  fill(44, 16, 4, 4, P.shirt);
  fill(48, 16, 4, 4, P.skin);
  for (const ax of [40, 44, 48, 52]) {
    fill(ax, 20, 4, 12, P.skin);
    fill(ax, 20, 4, 4, P.shirt);
  }

  // Leg (0,16): 4x12x4. Pants, then shoes.
  fill(4, 16, 4, 4, P.pants);
  fill(8, 16, 4, 4, P.shoes);
  for (const lx of [0, 4, 8, 12]) {
    fill(lx, 20, 4, 12, P.pants);
    fill(lx, 29, 4, 3, P.shoes);
  }
  return c;
}

/**
 * Assign Minecraft box-UVs to a BoxGeometry (w,h,d in skin pixels, region at u,v).
 * Model faces -z; +x is the player's right.
 */
export function mcBoxUV(geo: THREE.BoxGeometry, u: number, v: number, w: number, h: number, d: number, texW = 64, texH = 32) {
  const pos = geo.getAttribute("position");
  const uv = geo.getAttribute("uv");
  const box = new THREE.Box3().setFromBufferAttribute(pos as THREE.BufferAttribute);
  const size = box.getSize(new THREE.Vector3());
  // Face order in BoxGeometry: +x, -x, +y, -y, +z, -z.
  const regions: [number, number, number, number][] = [
    [u, v + d, d, h], // +x: right
    [u + d + w, v + d, d, h], // -x: left
    [u + d, v, w, d], // +y: top
    [u + d + w, v, w, d], // -y: bottom
    [u + d + w + d, v + d, w, h], // +z: back
    [u + d, v + d, w, h], // -z: front
  ];
  for (let face = 0; face < 6; face++) {
    const [ru, rv, rw, rh] = regions[face]!;
    for (let k = 0; k < 4; k++) {
      const i = face * 4 + k;
      const nx = (pos.getX(i) - box.min.x) / size.x;
      const ny = (pos.getY(i) - box.min.y) / size.y;
      const nz = (pos.getZ(i) - box.min.z) / size.z;
      let sCoord = 0, tCoord = 0;
      switch (face) {
        case 0: sCoord = 1 - nz; tCoord = ny; break;
        case 1: sCoord = nz; tCoord = ny; break;
        case 2: sCoord = 1 - nx; tCoord = nz; break;
        case 3: sCoord = 1 - nx; tCoord = nz; break;
        case 4: sCoord = nx; tCoord = ny; break;
        case 5: sCoord = 1 - nx; tCoord = ny; break;
      }
      uv.setXY(i, (ru + sCoord * rw) / texW, 1 - (rv + (1 - tCoord) * rh) / texH);
    }
  }
  uv.needsUpdate = true;
}
